import {
  getMeasureObjByOriginalID,
  getDimensionObjByID,
} from "../../utils/plotlyUtils";
import { sigviewFormat, capitalize } from "../../utils/utils";

const selectPrimaryCalendarData = (state, id) => {
  const wsForm = state.standaloneMsv;
  const startEpoch = wsForm.timeFilters.value.selectedDatesQE.startDate;
  const endEpoch = wsForm.timeFilters.value.selectedDatesQE.endDate;
  const finalMetricChartId = `${id}_chart_${startEpoch}_${endEpoch}`;
  const dataQEAll = wsForm.dataQE.value;
  const primaryCalendarData = dataQEAll[finalMetricChartId];
  return primaryCalendarData;
};

const selectSecondaryCalendarData = (state, id) => {
  const wsForm = state.standaloneMsv;
  const dataQEAll = wsForm.dataQE.value;
  const startEpoch = wsForm.timeFilters.value.selectedDatesQE.startDate;
  const endEpoch = wsForm.timeFilters.value.selectedDatesQE.endDate;
  const finalMetricChartId = `${id}_chart_${startEpoch}_${endEpoch}`;
  var secondaryCalendarData = [];
  for (const [key, value] of Object.entries(dataQEAll)) {
    if (key.startsWith(`${id}_chart_`) && key !== finalMetricChartId) {
      secondaryCalendarData.push(value);
    }
  }
  return secondaryCalendarData;
};

const selectMetricChartType = (state) =>
  state.standaloneMsv.metricChartType.value;

const selectLoadingStatusForWsMetricChart = (state, metricId) => {
  const wsForm = state.standaloneMsv;
  const dataQEAll = wsForm.dataQE.value;
  var allStatusForCurrMetric = [];
  for (const [key, value] of Object.entries(dataQEAll)) {
    if (key.startsWith(`${metricId}_chart_`)) {
      allStatusForCurrMetric.push(value.status);
    }
  }
  let partialLoading = allStatusForCurrMetric.some((el) => el === "loading");
  let partialSuccess = allStatusForCurrMetric.some((el) => el === "success");
  let fullLoading = allStatusForCurrMetric.every((el) => el === "loading");
  let fullSuccess = allStatusForCurrMetric.every((el) => el === "success");
  let anyError = allStatusForCurrMetric.some((el) => el === "error");
  let allError = allStatusForCurrMetric.every((el) => el === "error");

  return {
    partialLoading,
    partialSuccess,
    fullLoading,
    fullSuccess,
    anyError,
    allError,
  };
};

const selectErrorMessageForMsvMetricChart = (state, metricId) => {
  const msvMetricChartLoadingStatus = selectLoadingStatusForWsMetricChart(
    state,
    metricId
  );

  if (msvMetricChartLoadingStatus.anyError) {
    const wsForm = state.standaloneMsv;
    const dataQEAll = wsForm.dataQE.value;
    var allErrorMessages = [];
    for (const [key, value] of Object.entries(dataQEAll)) {
      if (key.startsWith(`${metricId}_chart_`)) {
        if (["error", "noData".includes(value.status)]) {
          allErrorMessages.push(value.message);
        }
      }
    }
    return allErrorMessages[0];
  } else {
    return "";
  }
};

const selectMetricAccessor = (state, id) => {
  const plotlyMetrics = state.data.plotlyMetrics;
  const metricObj = getMeasureObjByOriginalID(id, plotlyMetrics);
  const cmPattern = /CM[0-9]{3}/g;
  const isCmFlag = cmPattern.test(id);
  const accessor = isCmFlag ? id : metricObj["measureID"];
  return accessor;
};

const selecteMetricChartGranularity = (state) =>
  state.standaloneMsv.metricChartGranularity.value;

const selectSelectedTimezone = (state) =>
  state.standaloneMsv.timeFilters.value.selectedTimezone;

const selectReloadEpoch = (state, id) =>
  state.standaloneMsv.reloadEpochs.value[id];

const selectMetricObjectFromPlotlyMetrics = (state, id) => {
  const plotlyMetrics = state.data.plotlyMetrics;
  const metricObj = getMeasureObjByOriginalID(id, plotlyMetrics);
  return metricObj;
};

const selectMetricSelections = (state, id) => {
  const wsForm = state.standaloneMsv;
  const selectionsObject = wsForm.selectedKpis.value[id];
  const granularity = wsForm.metricChartGranularity.value;
  const granDimObject = getDimensionObjByID(
    granularity,
    wsForm.utils.value.plotlyDimensions
  );
  let selections = {
    chartType: wsForm.metricChartType.value,
    dimensionFilters: wsForm.dimensionFilters.value || [],
    timeFilters: wsForm.timeFilters.value || {},
    metricFilters: [], // HARD CODED
    dimensionsList: [granDimObject],
    metricsList: selectionsObject.metricsList || [],
    orderById: selectionsObject.orderById || "",
    orderBy: selectionsObject.orderBy || "",
    orderByType: selectionsObject.orderByType || "",
    percentCalList: selectionsObject.percentCalList || [],
    granularity: granularity,
  };

  return selections;
};

const selectMetricChartYaxisRangeType = (state) => {
  // TODO as it's currently not in standaloneMsv but making it configurable for metric charts
  return "auto"; // "auto" or "dynamic"
};

const selectMsvColorMapping = (state) =>
  state.standaloneMsv.msvColorMapping.value;

const selectAllSelectedTableItem = (state) =>
  state.standaloneMsv.allSelectedTableItem.value;

const selectSelectedTableItemInChart = (state) =>
  state.standaloneMsv.selectedTableItemInChart.value;

const selectActiveMsvTableId = (state) =>
  state.standaloneMsv.activeMsvTableId.value;

const selectMetricsInMsvTableAccessorsList = (state) => {
  const acitveMsvTableId = selectActiveMsvTableId(state);
  const metricsList =
    state.standaloneMsv.selectedDimensions.value[acitveMsvTableId].metricsList;
  const metricsListAccessorsList = metricsList.map((row) => {
    const id = row._id;
    const cmPattern = /CM[0-9]{3}/g;
    const isCmFlag = cmPattern.test(id);
    const accessor = isCmFlag ? id : row["measureID"];
    return accessor;
  });
  return metricsListAccessorsList;
};

const selectDataForSelectedTableItemInChart = (state, id) => {
  const msvForm = state.standaloneMsv;
  const dataQEAll = msvForm.dataQE.value;
  const selectedTableItemInChart = selectSelectedTableItemInChart(state);
  var dataForSelectedTableItemInChart = {};
  for (const selectedItem of selectedTableItemInChart) {
    const msvDataQeKey = `${id}_chart_${selectedItem}`;
    const dataQeItem = dataQEAll[msvDataQeKey];
    dataForSelectedTableItemInChart[msvDataQeKey] = dataQeItem;
  }
  return dataForSelectedTableItemInChart;
};

const selectMetricChartHeaderDetails = (state, id, themeColors, user) => {
  const plotlyMetrics = state.data.plotlyMetrics;
  const wsForm = state.standaloneMsv;
  const selectionsObject = wsForm.selectedKpis.value[id];
  const dataQEAll = wsForm.dataQE.value;
  const metricData = dataQEAll[id];
  const metricObj = getMeasureObjByOriginalID(id, plotlyMetrics);
  const cmPattern = /CM[0-9]{3}/g;
  const isCmFlag = cmPattern.test(id);
  let infoTooltipStatus = false;

  if (
    user.reqMetadata.organization === "Kayzen" &&
    user.reqMetadata.view === "Percentile"
  ) {
    infoTooltipStatus =
      (metricObj["displayFormula"] !== undefined &&
        metricObj["displayFormula"].startsWith("(Price Digest")) ||
      (metricObj["displayFormula"] !== undefined &&
        metricObj["displayFormula"].startsWith("(price digest"));
  }
  var obj = {};
  obj["isCmFlag"] = isCmFlag;
  obj["title"] = selectionsObject.metricsList[0]["_title"];
  obj["value"] = metricData.result.displayValue;
  obj["actualValue"] = dataQEAll[id].result.value;
  obj["totalValueStatus"] = metricData.status;
  obj["tickerdeltaPercentageStatus"] = `${
    dataQEAll[`${id}_deltaPercentage`].status
  }`;
  obj["tickertrueDeltaStatus"] = `${dataQEAll[`${id}_trueDelta`].status}`;
  obj["tickerChange"] = `${
    dataQEAll[`${id}_deltaPercentage`].result.displayValue
  } ${dataQEAll[`${id}_trueDelta`].result.displayValue}`;
  obj["tickertype"] =
    Number(dataQEAll[`${id}_trueDelta`].result.value) < 0
      ? "negative"
      : "positive";
  obj["isDeleteVisible"] = Object.keys(wsForm.selectedKpis.value).length > 1;
  obj["infoFlag"] = infoTooltipStatus ? false : isCmFlag;
  obj["infoTooltip"] = isCmFlag ? metricObj["displayFormula"] : "";
  obj["tickerIconName"] =
    obj["tickertype"] === "positive" ? "arrow_drop_up" : "arrow_drop_down";
  obj["showTickerChange"] = wsForm.timeFilters.value.isComparisonOn;
  obj["tickerStatus"] =
    obj["tickertrueDeltaStatus"] === "success" &&
    obj["tickerdeltaPercentageStatus"] === "success"
      ? true
      : false;
  obj["formattedValue"] = sigviewFormat(obj["actualValue"]);
  obj["tickerColor"] =
    obj["tickertype"] === "negative"
      ? themeColors["negChangeColor"]
      : themeColors["posChangeColor"];
  return obj;
};

const selectMetricCounterData = (state, id) => {
  const wsForm = state.standaloneMsv;
  const dataQEAll = wsForm.dataQE.value;
  const metricCounterData = dataQEAll[id];
  return metricCounterData;
};

const selectDimTableData = (state, id) => {
  const wsForm = state.standaloneMsv;
  const dataQEAll = wsForm.dataQE.value;
  const dimTableData = dataQEAll[id];
  return dimTableData;
};

const selectMsvTableSelections = (state, id) => {
  const msvForm = state.standaloneMsv;
  const selectionsObject = msvForm.selectedDimensions.value[id];
  var dimensionFilters =
    msvForm.dimensionFilters.value.filter((el) => el.id !== id) || [];
  // Add regex search item
  if (msvForm.regexSearchField.value.length > 0) {
    var regexSearchItem = {
      id: id,
      values: [`${msvForm.regexSearchField.value}`],
      metadata: selectionsObject.dimensionsList[0],
      filterType: "regex",
      advancedFilters: [],
      valid: true,
    };
    dimensionFilters = [...dimensionFilters, regexSearchItem];
  }
  const selections = {
    chartType: "table", //! HARD CODED TO TABLE FOR ANALYZE selectionsObject.chartType,
    dimensionFilters: dimensionFilters,
    timeFilters: msvForm.timeFilters.value || {},
    metricFilters: selectionsObject.metricFilters,
    dimensionsList: selectionsObject.dimensionsList || [],
    metricsList: selectionsObject.metricsList || [],
    orderById:
      selectionsObject.orderById === "_dimension"
        ? id
        : selectionsObject.orderById,
    orderBy: selectionsObject.orderBy || "asc",
    orderByType:
      selectionsObject.orderById === "_dimension"
        ? "id_only"
        : selectionsObject.orderByType,
    percentCalList: selectionsObject.percentCalList || [],
    dataLimit: msvForm.dataLimit.value,
    regexSearch: msvForm.regexSearchField.value,
    granularity: msvForm.metricChartGranularity.value,
  };
  return selections;
};

const selectIsDimTableDeleteVisible = (state) =>
  Object.keys(state.standaloneMsv.selectedDimensions.value).length > 1;

const selectDimTableSelections = (state, id) => {
  const wsForm = state.standaloneMsv;
  const selectionsObject = wsForm.selectedDimensions.value[id];
  const selections = {
    chartType: "table", //! HARD CODED TO TABLE FOR ANALYZE selectionsObject.chartType,
    dimensionFilters: wsForm.dimensionFilters.value || [],
    timeFilters: wsForm.timeFilters.value || {},
    metricFilters: [], // HARD CODED
    dimensionsList: selectionsObject.dimensionsList || [],
    metricsList: selectionsObject.metricsList || [],
    orderById:
      selectionsObject.orderById === "_dimension"
        ? id
        : selectionsObject.orderById,
    orderBy: selectionsObject.orderBy || "asc",
    orderByType:
      selectionsObject.orderById === "_dimension"
        ? "id_only"
        : selectionsObject.orderByType,
    percentCalList: selectionsObject.percentCalList || [],
    dataLimit: wsForm.dataLimit.value,
  };

  return selections;
};

const selectDimTableFilterDetails = (state, id, themeColors) => {
  const selections = selectDimTableSelections(state, id);
  const filterAppliedOnDimTableRow = selections.dimensionFilters.find(
    (row) => row.metadata._id === selections.dimensionsList[0]._id
  );
  const filterAppliedOnDimTable =
    filterAppliedOnDimTableRow === undefined ? false : true;
  var filterAppliedOnDimTableType = "include";
  var isAdvancedFiltersApplied = false;
  if (filterAppliedOnDimTable) {
    filterAppliedOnDimTableType = filterAppliedOnDimTableRow.filterType;
    isAdvancedFiltersApplied =
      filterAppliedOnDimTableRow.advancedFilters.length > 0;
  }
  const filterIconColor =
    filterAppliedOnDimTableType === "include"
      ? themeColors["primaryColor"]
      : themeColors["secondaryColorLight"];
  const filterIconTooltip = `${capitalize(
    filterAppliedOnDimTableType
  )} Filters`;
  const isFullViewDisabled =
    isAdvancedFiltersApplied || filterAppliedOnDimTableType === "exclude";
  const fullViewTooltip = isFullViewDisabled
    ? "Full view disabled for exclude and advanced filters"
    : "Full View";

  const dimTableFilterDetails = {
    filterAppliedOnDimTableRow,
    filterAppliedOnDimTableType,
    isAdvancedFiltersApplied,
    filterIconColor,
    filterIconTooltip,
    fullViewTooltip,
    isFullViewDisabled,
  };

  return dimTableFilterDetails;
};

export {
  selectPrimaryCalendarData,
  selectSecondaryCalendarData,
  selectMetricChartType,
  selectLoadingStatusForWsMetricChart,
  selectMetricAccessor,
  selecteMetricChartGranularity,
  selectSelectedTimezone,
  selectMetricObjectFromPlotlyMetrics,
  selectMetricChartYaxisRangeType,
  selectMetricChartHeaderDetails,
  selectMetricCounterData,
  selectMetricSelections,
  selectDimTableSelections,
  selectDimTableData,
  selectDimTableFilterDetails,
  selectReloadEpoch,
  selectIsDimTableDeleteVisible,
  selectMsvColorMapping,
  selectSelectedTableItemInChart,
  selectAllSelectedTableItem,
  selectDataForSelectedTableItemInChart,
  selectMsvTableSelections,
  selectErrorMessageForMsvMetricChart,
  selectMetricsInMsvTableAccessorsList,
};
